import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Table,
  Button,
  Badge,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Modal,
  Stack,
  Switch,
  TextInput,
  Select,
  Textarea,
  LoadingOverlay,
  Box,
  Divider,
  ScrollArea,
  Accordion,
  Grid,
  NavLink,
  Center,
  ThemeIcon,
  Alert,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconTrashX,
  IconArrowLeft,
  IconFileText,
  IconGripVertical,
  IconX,
  IconLetterCase,
  IconNumbers,
  IconCalendar,
  IconList,
  IconAlignLeft,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { templatesApi } from '@/services/templates.api';
import { useAuthStore } from '@/stores/authStore';
import type {
  DocumentTemplate,
  DocumentTemplateListItem,
  TemplateFieldCreate,
  FieldType,
} from '@/types/template.types';

const fieldTypeLabels: Record<FieldType, string> = {
  text: 'Texto',
  number: 'Número',
  date: 'Data',
  enum: 'Lista',
  textarea: 'Texto Longo',
};

const fieldTypeColors: Record<FieldType, string> = {
  text: 'blue',
  number: 'green',
  date: 'orange',
  enum: 'violet',
  textarea: 'cyan',
};

const fieldTypeIcons: Record<FieldType, React.ComponentType<{ size?: number }>> = {
  text: IconLetterCase,
  number: IconNumbers,
  date: IconCalendar,
  enum: IconList,
  textarea: IconAlignLeft,
};

const emptyField: TemplateFieldCreate = {
  name: '',
  displayName: '',
  fieldType: 'text',
  fieldOrder: 0,
  groupName: 'Geral',
  description: '',
  llmPromptHint: '',
  enumValues: [],
  isRequired: true,
};

// Componente para item sortável da lista de campos
interface SortableFieldItemProps {
  field: TemplateFieldCreate;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  canDelete: boolean;
}

const SortableFieldItem = ({
  field,
  index,
  isSelected,
  onSelect,
  onDelete,
  canDelete,
}: SortableFieldItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `field-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const FieldIcon = fieldTypeIcons[field.fieldType];

  return (
    <div ref={setNodeRef} style={style}>
      <NavLink
        active={isSelected}
        onClick={onSelect}
        label={field.displayName || `Campo ${index + 1}`}
        leftSection={
          <Group gap={4}>
            <ActionIcon
              size="xs"
              variant="subtle"
              {...attributes}
              {...listeners}
              style={{ cursor: 'grab' }}
            >
              <IconGripVertical size={14} />
            </ActionIcon>
            <ThemeIcon size="sm" variant="light" color={fieldTypeColors[field.fieldType]}>
              <FieldIcon size={14} />
            </ThemeIcon>
          </Group>
        }
        rightSection={
          canDelete ? (
            <ActionIcon
              size="xs"
              variant="subtle"
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
        styles={{
          root: {
            borderRadius: 'var(--mantine-radius-sm)',
            marginBottom: 4,
          },
        }}
      />
    </div>
  );
};

export const TemplatesPanel = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [templates, setTemplates] = useState<DocumentTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null);

  // Form states
  const [formTitle, setFormTitle] = useState('');
  const [formFields, setFormFields] = useState<TemplateFieldCreate[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);

  // Enum values input
  const [enumInput, setEnumInput] = useState<Record<number, string>>({});

  // Deactivate modal state
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [templateToDeactivate, setTemplateToDeactivate] = useState<DocumentTemplateListItem | null>(null);

  // Permanent delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplateListItem | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Sensores para drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await templatesApi.getAll(isAdmin());
      setTemplates(data);
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao carregar templates',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!formTitle.trim()) {
      notifications.show({
        title: 'Erro',
        message: 'Título é obrigatório',
        color: 'red',
      });
      return;
    }

    if (formFields.length === 0) {
      notifications.show({
        title: 'Erro',
        message: 'Adicione pelo menos um campo',
        color: 'red',
      });
      return;
    }

    // Validate fields
    for (const field of formFields) {
      if (!field.name.trim() || !field.displayName.trim()) {
        notifications.show({
          title: 'Erro',
          message: 'Todos os campos devem ter nome e nome de exibição',
          color: 'red',
        });
        return;
      }
    }

    try {
      setSubmitting(true);
      await templatesApi.create({
        title: formTitle,
        fields: formFields.map((f, idx) => ({ ...f, fieldOrder: idx })),
      });

      notifications.show({
        title: 'Sucesso',
        message: 'Template criado com sucesso',
        color: 'green',
      });

      setCreateModalOpen(false);
      resetForm();
      loadTemplates();
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao criar template',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;

    if (!formTitle.trim()) {
      notifications.show({
        title: 'Erro',
        message: 'Título é obrigatório',
        color: 'red',
      });
      return;
    }

    try {
      setSubmitting(true);
      await templatesApi.update(selectedTemplate.id, {
        title: formTitle,
        fields: formFields.map((f, idx) => ({ ...f, fieldOrder: idx })),
      });

      notifications.show({
        title: 'Sucesso',
        message: 'Template atualizado (nova versão criada)',
        color: 'green',
      });

      setEditModalOpen(false);
      setSelectedTemplate(null);
      resetForm();
      loadTemplates();
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao atualizar template',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openDeactivateModal = (template: DocumentTemplateListItem) => {
    setTemplateToDeactivate(template);
    setDeactivateModalOpen(true);
  };

  const handleDeactivateTemplate = async () => {
    if (!templateToDeactivate) return;

    try {
      await templatesApi.deactivate(templateToDeactivate.id);

      notifications.show({
        title: 'Sucesso',
        message: 'Template desativado com sucesso',
        color: 'green',
      });

      setDeactivateModalOpen(false);
      setTemplateToDeactivate(null);
      loadTemplates();
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao desativar template',
        color: 'red',
      });
    }
  };

  const openDeleteModal = (template: DocumentTemplateListItem) => {
    setTemplateToDelete(template);
    setDeleteConfirmText('');
    setDeleteModalOpen(true);
  };

  const handlePermanentDeleteTemplate = async () => {
    if (!templateToDelete) return;

    if (deleteConfirmText !== templateToDelete.title) {
      notifications.show({
        title: 'Erro',
        message: 'Digite o nome do template corretamente para confirmar',
        color: 'red',
      });
      return;
    }

    try {
      setDeleting(true);
      const result = await templatesApi.deletePermanently(templateToDelete.id);

      notifications.show({
        title: 'Template excluído permanentemente',
        message: `${result.deletedRooms} salas, ${result.deletedFillings} preenchimentos e ${result.deletedMessages} mensagens foram removidos`,
        color: 'green',
      });

      setDeleteModalOpen(false);
      setTemplateToDelete(null);
      setDeleteConfirmText('');
      loadTemplates();
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao excluir template permanentemente',
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const openViewModal = async (template: DocumentTemplateListItem) => {
    try {
      setLoading(true);
      const fullTemplate = await templatesApi.getById(template.id);
      setSelectedTemplate(fullTemplate);
      setViewModalOpen(true);
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: 'Erro ao carregar detalhes do template',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (template: DocumentTemplateListItem) => {
    try {
      setLoading(true);
      const fullTemplate = await templatesApi.getById(template.id);
      setSelectedTemplate(fullTemplate);
      setFormTitle(fullTemplate.title);
      setFormFields(
        fullTemplate.fields.map((f) => ({
          name: f.name,
          displayName: f.displayName,
          fieldType: f.fieldType,
          fieldOrder: f.fieldOrder,
          groupName: f.groupName,
          description: f.description || '',
          llmPromptHint: f.llmPromptHint || '',
          enumValues: f.enumValues || [],
          isRequired: f.isRequired,
        }))
      );
      setSelectedFieldIndex(fullTemplate.fields.length > 0 ? 0 : null);
      setEditModalOpen(true);
    } catch (error: any) {
      notifications.show({
        title: 'Erro',
        message: 'Erro ao carregar template para edição',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormTitle('');
    setFormFields([]);
    setEnumInput({});
    setSelectedFieldIndex(null);
  };

  const addField = () => {
    const newIndex = formFields.length;
    setFormFields([...formFields, { ...emptyField, fieldOrder: newIndex }]);
    setSelectedFieldIndex(newIndex);
  };

  const removeField = (index: number) => {
    if (formFields.length === 1) return;
    setFormFields(formFields.filter((_, i) => i !== index));

    // Gerenciar seleção após exclusão
    if (selectedFieldIndex === index) {
      setSelectedFieldIndex(index > 0 ? index - 1 : formFields.length > 1 ? 0 : null);
    } else if (selectedFieldIndex !== null && selectedFieldIndex > index) {
      setSelectedFieldIndex(selectedFieldIndex - 1);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = parseInt(String(active.id).replace('field-', ''));
      const newIndex = parseInt(String(over.id).replace('field-', ''));

      setFormFields((fields) => arrayMove(fields, oldIndex, newIndex));

      // Atualizar índice selecionado para acompanhar o item movido
      if (selectedFieldIndex === oldIndex) {
        setSelectedFieldIndex(newIndex);
      } else if (selectedFieldIndex !== null) {
        if (oldIndex < selectedFieldIndex && newIndex >= selectedFieldIndex) {
          setSelectedFieldIndex(selectedFieldIndex - 1);
        } else if (oldIndex > selectedFieldIndex && newIndex <= selectedFieldIndex) {
          setSelectedFieldIndex(selectedFieldIndex + 1);
        }
      }
    }
  };

  const updateField = (index: number, updates: Partial<TemplateFieldCreate>) => {
    setFormFields(
      formFields.map((field, i) => (i === index ? { ...field, ...updates } : field))
    );
  };

  const addEnumValue = (fieldIndex: number) => {
    const value = enumInput[fieldIndex]?.trim();
    if (!value) return;

    const field = formFields[fieldIndex];
    const currentValues = field.enumValues || [];
    if (!currentValues.includes(value)) {
      updateField(fieldIndex, { enumValues: [...currentValues, value] });
    }
    setEnumInput({ ...enumInput, [fieldIndex]: '' });
  };

  const removeEnumValue = (fieldIndex: number, valueIndex: number) => {
    const field = formFields[fieldIndex];
    const newValues = (field.enumValues || []).filter((_, i) => i !== valueIndex);
    updateField(fieldIndex, { enumValues: newValues });
  };

  // Renderiza o painel esquerdo com a lista de campos
  const renderFieldsList = () => (
    <Paper withBorder p="xs" style={{ height: '100%' }}>
      <Stack gap="xs">
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addField} fullWidth size="sm">
          Adicionar Campo
        </Button>
        <Divider />
        <ScrollArea.Autosize mah={320} offsetScrollbars>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={formFields.map((_, i) => `field-${i}`)} strategy={verticalListSortingStrategy}>
              {formFields.map((field, index) => (
                <SortableFieldItem
                  key={`field-${index}`}
                  field={field}
                  index={index}
                  isSelected={selectedFieldIndex === index}
                  onSelect={() => setSelectedFieldIndex(index)}
                  onDelete={() => removeField(index)}
                  canDelete={formFields.length > 1}
                />
              ))}
            </SortableContext>
          </DndContext>
        </ScrollArea.Autosize>
      </Stack>
    </Paper>
  );

  // Renderiza o painel direito com o formulário do campo selecionado
  const renderFieldDetail = () => {
    // Mensagem quando nenhum campo está selecionado
    if (selectedFieldIndex === null || !formFields[selectedFieldIndex]) {
      return (
        <Paper withBorder p="xl" style={{ height: '100%' }}>
          <Center style={{ height: '100%', minHeight: 300 }}>
            <Stack align="center" gap="xs">
              <IconFileText size={48} color="gray" opacity={0.5} />
              <Text c="dimmed" ta="center">
                Selecione um campo ou clique em{' '}
                <Text span fw={500}>
                  + Adicionar Campo
                </Text>
              </Text>
            </Stack>
          </Center>
        </Paper>
      );
    }

    const field = formFields[selectedFieldIndex];
    const index = selectedFieldIndex;

    return (
      <Paper withBorder p="md" style={{ height: '100%' }}>
        <ScrollArea.Autosize mah={380} offsetScrollbars>
          <Stack gap="sm">
            <Group grow>
              <TextInput
                label="Nome interno"
                placeholder="nome_do_campo"
                value={field.name}
                onChange={(e) =>
                  updateField(index, {
                    name: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                  })
                }
                required
              />
              <TextInput
                label="Nome de exibição"
                placeholder="Nome do Campo"
                value={field.displayName}
                onChange={(e) => updateField(index, { displayName: e.target.value })}
                required
              />
            </Group>

            <Group grow>
              <Select
                label="Tipo"
                value={field.fieldType}
                onChange={(value) =>
                  updateField(index, { fieldType: (value as FieldType) || 'text' })
                }
                data={Object.entries(fieldTypeLabels).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
              <TextInput
                label="Grupo"
                placeholder="Geral"
                value={field.groupName}
                onChange={(e) => updateField(index, { groupName: e.target.value })}
              />
            </Group>

            <Textarea
              label="Descrição"
              placeholder="Descrição do campo para o usuário"
              value={field.description}
              onChange={(e) => updateField(index, { description: e.target.value })}
              minRows={2}
            />

            <Textarea
              label="Dica para LLM"
              placeholder="Instruções para o agente de IA extrair este campo"
              value={field.llmPromptHint}
              onChange={(e) => updateField(index, { llmPromptHint: e.target.value })}
              minRows={2}
            />

            {field.fieldType === 'enum' && (
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Valores da lista
                </Text>
                <Group gap="xs" mb="xs">
                  <TextInput
                    placeholder="Adicionar valor"
                    value={enumInput[index] || ''}
                    onChange={(e) => setEnumInput({ ...enumInput, [index]: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && addEnumValue(index)}
                    style={{ flex: 1 }}
                  />
                  <Button size="sm" onClick={() => addEnumValue(index)}>
                    Adicionar
                  </Button>
                </Group>
                <Group gap="xs">
                  {(field.enumValues || []).map((value, vi) => (
                    <Badge
                      key={vi}
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() => removeEnumValue(index, vi)}
                        >
                          <IconX size={12} />
                        </ActionIcon>
                      }
                    >
                      {value}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}

            <Switch
              label="Campo obrigatório"
              checked={field.isRequired}
              onChange={(e) => updateField(index, { isRequired: e.target.checked })}
            />
          </Stack>
        </ScrollArea.Autosize>
      </Paper>
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between">
          <Group>
            <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/dashboard')}>
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={2}>Gerenciamento de Templates</Title>
          </Group>
          <Button leftSection={<IconPlus size={18} />} onClick={() => setCreateModalOpen(true)}>
            Novo Template
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" pos="relative">
          <LoadingOverlay visible={loading} />

          {templates.length === 0 && !loading ? (
            <Box ta="center" py="xl">
              <Text c="dimmed">Nenhum template encontrado</Text>
            </Box>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Título</Table.Th>
                  <Table.Th ta="center">Versão</Table.Th>
                  <Table.Th ta="center">Campos</Table.Th>
                  <Table.Th ta="center">Status</Table.Th>
                  <Table.Th ta="center">Criado em</Table.Th>
                  <Table.Th ta="center" style={{ width: 150 }}>Ações</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {templates.map((template) => (
                  <Table.Tr key={template.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <IconFileText size={18} />
                        {template.title}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Badge variant="light">v{template.version}</Badge>
                    </Table.Td>
                    <Table.Td ta="center">{template.fieldCount} campos</Table.Td>
                    <Table.Td ta="center">
                      <Badge color={template.isActive ? 'green' : 'gray'} variant="dot">
                        {template.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="center">
                      {new Date(template.createdAt).toLocaleDateString('pt-BR')}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap" justify="center">
                        <Tooltip label="Ver detalhes">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => openViewModal(template)}
                          >
                            <IconFileText size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Editar (nova versão)">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => openEditModal(template)}
                            disabled={!template.isActive}
                          >
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>
                        {isAdmin() && (
                          <>
                            <Tooltip label="Desativar">
                              <ActionIcon
                                variant="subtle"
                                color="orange"
                                onClick={() => openDeactivateModal(template)}
                                disabled={!template.isActive}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Excluir permanentemente">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => openDeleteModal(template)}
                              >
                                <IconTrashX size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Modal: Criar Template */}
      <Modal
        opened={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          resetForm();
        }}
        title="Criar Novo Template"
        size={1400}
      >
        <Stack gap="md">
          <TextInput
            label="Título do Template"
            placeholder="Ex: Requisitos de Integração"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            required
          />

          <Divider label="Campos do Template" labelPosition="center" />

          <Grid gutter="md">
            <Grid.Col span={4}>{renderFieldsList()}</Grid.Col>
            <Grid.Col span={8}>{renderFieldDetail()}</Grid.Col>
          </Grid>

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setCreateModalOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleCreateTemplate} loading={submitting}>
              Criar Template
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Editar Template */}
      <Modal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedTemplate(null);
          resetForm();
        }}
        title={`Editar Template - ${selectedTemplate?.title} (v${selectedTemplate?.version})`}
        size={1400}
      >
        <Text size="sm" c="dimmed" mb="md">
          Editar criará uma nova versão do template. A versão anterior será mantida para
          preenchimentos existentes.
        </Text>

        <Stack gap="md">
          <TextInput
            label="Título do Template"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            required
          />

          <Divider label="Campos do Template" labelPosition="center" />

          <Grid gutter="md">
            <Grid.Col span={4}>{renderFieldsList()}</Grid.Col>
            <Grid.Col span={8}>{renderFieldDetail()}</Grid.Col>
          </Grid>

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedTemplate(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpdateTemplate} loading={submitting}>
              Salvar Nova Versão
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Ver Detalhes */}
      <Modal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedTemplate(null);
        }}
        title={`${selectedTemplate?.title} (v${selectedTemplate?.version})`}
        size="lg"
      >
        {selectedTemplate && (
          <Stack gap="md">
            <Group>
              <Badge color={selectedTemplate.isActive ? 'green' : 'gray'}>
                {selectedTemplate.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
              <Text size="sm" c="dimmed">
                Criado em {new Date(selectedTemplate.createdAt).toLocaleDateString('pt-BR')}
              </Text>
            </Group>

            <Divider label={`${selectedTemplate.fields.length} Campos`} labelPosition="center" />

            <Accordion>
              {selectedTemplate.fields.map((field) => (
                <Accordion.Item key={field.id} value={field.name}>
                  <Accordion.Control>
                    <Group gap="sm">
                      <Badge color={fieldTypeColors[field.fieldType]} size="sm">
                        {fieldTypeLabels[field.fieldType]}
                      </Badge>
                      <Text fw={500}>{field.displayName}</Text>
                      {field.isRequired && (
                        <Badge color="red" size="xs" variant="outline">
                          Obrigatório
                        </Badge>
                      )}
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="xs">
                      <Text size="sm">
                        <strong>Nome interno:</strong> {field.name}
                      </Text>
                      <Text size="sm">
                        <strong>Grupo:</strong> {field.groupName}
                      </Text>
                      {field.description && (
                        <Text size="sm">
                          <strong>Descrição:</strong> {field.description}
                        </Text>
                      )}
                      {field.llmPromptHint && (
                        <Text size="sm">
                          <strong>Dica LLM:</strong> {field.llmPromptHint}
                        </Text>
                      )}
                      {field.enumValues && field.enumValues.length > 0 && (
                        <Box>
                          <Text size="sm" fw={500}>
                            Valores:
                          </Text>
                          <Group gap="xs">
                            {field.enumValues.map((v, i) => (
                              <Badge key={i} variant="light">
                                {v}
                              </Badge>
                            ))}
                          </Group>
                        </Box>
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setViewModalOpen(false)}>
                Fechar
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal: Confirmar Desativação */}
      <Modal
        opened={deactivateModalOpen}
        onClose={() => {
          setDeactivateModalOpen(false);
          setTemplateToDeactivate(null);
        }}
        title="Confirmar Desativação"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Tem certeza que deseja desativar o template <Text span fw={600}>"{templateToDeactivate?.title}"</Text>?
          </Text>
          <Text size="sm" c="dimmed">
            O template não poderá ser usado para criar novas salas, mas os dados existentes serão mantidos.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setDeactivateModalOpen(false);
                setTemplateToDeactivate(null);
              }}
            >
              Cancelar
            </Button>
            <Button color="orange" onClick={handleDeactivateTemplate}>
              Desativar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Confirmar Exclusão Permanente */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTemplateToDelete(null);
          setDeleteConfirmText('');
        }}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={24} color="red" />
            <Text fw={600}>Excluir Template Permanentemente</Text>
          </Group>
        }
        size="md"
      >
        {templateToDelete && (
          <Stack gap="md">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={20} />}>
              <Text fw={500} mb="xs">Esta ação é IRREVERSÍVEL!</Text>
              <Text size="sm">
                Ao excluir o template "{templateToDelete.title}" permanentemente, todos os seguintes dados serão removidos:
              </Text>
              <Box component="ul" style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Todas as salas criadas com este template</li>
                <li>Todos os preenchimentos de documentos</li>
                <li>Todas as mensagens de chat e transcrições</li>
                <li>Todos os campos do template</li>
              </Box>
            </Alert>

            <Box>
              <Text size="sm" mb="xs">
                Para confirmar, digite o nome do template: <Text span fw={600}>"{templateToDelete.title}"</Text>
              </Text>
              <TextInput
                placeholder={templateToDelete.title}
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                error={deleteConfirmText.length > 0 && deleteConfirmText !== templateToDelete.title ? 'Nome não corresponde' : undefined}
              />
            </Box>

            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setTemplateToDelete(null);
                  setDeleteConfirmText('');
                }}
              >
                Cancelar
              </Button>
              <Button
                color="red"
                onClick={handlePermanentDeleteTemplate}
                loading={deleting}
                disabled={deleteConfirmText !== templateToDelete.title}
                leftSection={<IconTrashX size={18} />}
              >
                Excluir Permanentemente
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};
